// Quick task 260922-mmg: Force App Update ("App Settings") page hook.
//
// D-01: NO enabled column and no on/off switch — a platform is active
// (blocks below-minimum installs) purely by having a minimum_version OR
// minimum_build set. D-05: before saving with at least one active
// platform, confirm with the user, naming who will be blocked; save
// directly when both platforms will end up empty (the "emergency
// release", D-03).
import { useEffect, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import { getAppSettings, updateAppSettings } from 'app/api/appSettings';
import { appSettingsValidationRules, validate } from 'app/constants/validation-constants';
import Message from 'app/utils/Message';
import Toast from 'app/utils/Toast';

// Mirrors AppSettingsService.ts's APP_BUILD_MAX (int32 max) — the
// optionalPattern regex only rules out non-positive-integer shapes, so the
// upper-bound check is added here on top of `validate()`.
export const BUILD_MAX = 2147483647;

const EMPTY_VALUES = {
  android_minimum_version: '',
  android_minimum_build: '',
  android_store_url: '',
  ios_minimum_version: '',
  ios_minimum_build: '',
  ios_store_url: '',
  title: '',
  message: '',
  button_text: '',
};

// AdminView -> form state. Nulls become '' (NewInput/Textarea always want a
// string), builds become strings (an <input type="text"> value), store
// URLs are copied through read-only.
export function toFormValues(data) {
  if (!data) return { ...EMPTY_VALUES };
  return {
    android_minimum_version: data.android_minimum_version ?? '',
    android_minimum_build:
      data.android_minimum_build === null || data.android_minimum_build === undefined
        ? ''
        : String(data.android_minimum_build),
    android_store_url: data.android_store_url ?? '',
    ios_minimum_version: data.ios_minimum_version ?? '',
    ios_minimum_build:
      data.ios_minimum_build === null || data.ios_minimum_build === undefined
        ? ''
        : String(data.ios_minimum_build),
    ios_store_url: data.ios_store_url ?? '',
    title: data.title ?? '',
    message: data.message ?? '',
    button_text: data.button_text ?? '',
  };
}

// Form state -> PUT body. Trims every editable field, '' becomes null,
// builds become numbers, and only the seven editable keys are ever sent
// (no store URLs — the server ignores them anyway, but the page never
// sends them at all).
export function toPayload(values) {
  const trim = (v) => (typeof v === 'string' ? v.trim() : v);
  const androidBuild = trim(values.android_minimum_build);
  const iosBuild = trim(values.ios_minimum_build);
  return {
    android_minimum_version: trim(values.android_minimum_version) || null,
    android_minimum_build: androidBuild ? Number(androidBuild) : null,
    ios_minimum_version: trim(values.ios_minimum_version) || null,
    ios_minimum_build: iosBuild ? Number(iosBuild) : null,
    title: trim(values.title) || null,
    message: trim(values.message) || null,
    button_text: trim(values.button_text) || null,
  };
}

// Mirrors AppSettingsService.ts's parseAppSettingsInput. Runs the shared
// rules (format + length) then adds the build range check the regex alone
// cannot express.
export function validateAppSettings(values) {
  const trimmed = {
    ...values,
    android_minimum_version: (values.android_minimum_version || '').trim(),
    android_minimum_build: (values.android_minimum_build || '').trim(),
    ios_minimum_version: (values.ios_minimum_version || '').trim(),
    ios_minimum_build: (values.ios_minimum_build || '').trim(),
    title: (values.title || '').trim(),
    message: (values.message || '').trim(),
    button_text: (values.button_text || '').trim(),
  };

  const errors = validate(trimmed, appSettingsValidationRules);

  const buildMessage = (label) => `${label} minimum build must be a whole number from 1 to 2147483647`;
  if (
    !errors.android_minimum_build &&
    trimmed.android_minimum_build &&
    /^[1-9]\d*$/.test(trimmed.android_minimum_build) &&
    Number(trimmed.android_minimum_build) > BUILD_MAX
  ) {
    errors.android_minimum_build = buildMessage('Android');
  }
  if (
    !errors.ios_minimum_build &&
    trimmed.ios_minimum_build &&
    /^[1-9]\d*$/.test(trimmed.ios_minimum_build) &&
    Number(trimmed.ios_minimum_build) > BUILD_MAX
  ) {
    errors.ios_minimum_build = buildMessage('iOS');
  }

  return errors;
}

// Names what will be blocked, per the D-05 confirm sentence. version set ->
// "<Android|iOS> below <version>"; else build set -> "<Android|iOS> below
// build <build>"; neither set -> that platform contributes nothing. Returns
// null when nothing is active (save without confirmation).
export function describeBlockedUsers(payload) {
  const parts = [];
  if (payload.android_minimum_version) {
    parts.push(`Android below ${payload.android_minimum_version}`);
  } else if (payload.android_minimum_build) {
    parts.push(`Android below build ${payload.android_minimum_build}`);
  }
  if (payload.ios_minimum_version) {
    parts.push(`iOS below ${payload.ios_minimum_version}`);
  } else if (payload.ios_minimum_build) {
    parts.push(`iOS below build ${payload.ios_minimum_build}`);
  }
  if (parts.length === 0) return null;
  return `Users on ${parts.join(' and ')} will be blocked from using the app until they update. Continue?`;
}

const useAppSettings = () => {
  const [values, setValues] = useState({ ...EMPTY_VALUES });
  const [errors, setErrors] = useState({});
  const [pageLoading, setPageLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function load() {
    setPageLoading(true);
    const response = await getAppSettings();
    setPageLoading(false);
    if (response?.responseCode === 200) {
      setValues(toFormValues(response.data));
    } else {
      Toast.error(response?.message || Message.Error.COMMON_MESSAGE);
    }
  }

  function handleChange(e) {
    if (!e || !e.target) return;
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  }

  async function submit(payload) {
    setSaving(true);
    const response = await updateAppSettings(payload);
    setSaving(false);
    if (response?.responseCode === 200) {
      Toast.success('App settings saved successfully');
      setValues(toFormValues(response.data));
    } else {
      Toast.error(response?.message || Message.Error.COMMON_MESSAGE);
    }
  }

  function handleSave(e) {
    if (e && e.preventDefault) e.preventDefault();

    const validationErrors = validateAppSettings(values);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    const payload = toPayload(values);

    const confirmMessage = describeBlockedUsers(payload);
    if (confirmMessage) {
      confirmAlert({
        title: '',
        message: confirmMessage,
        buttons: [
          { label: 'Yes', onClick: () => submit(payload) },
          { label: 'No' },
        ],
        overlayClassName: 'overlay-custom-class-name',
      });
    } else {
      submit(payload);
    }
  }

  return {
    values,
    errors,
    pageLoading,
    saving,
    handleChange,
    handleSave,
  };
};

export default useAppSettings;
